/**
 * SFOC curve selection model. Curves are identified by an opaque key
 * `ship‖engine‖fuel`. For ships that only have generic (non-per-engine) curves,
 * the engine slot is the constant `GENERIC_ENGINE`.
 *
 * Pure helpers here build the per-ship engine×fuel matrix, enumerate the keys
 * for a ship / engine / fuel group, and compute a group's tri-state (none /
 * partial / all) so the sidebar headers can reflect mixed selections.
 */

import type { SfocCurve, SfocCurveVersion, SfocCurves } from '../../api/types.ts';
import { hexToHsl, hslToHex } from '../plot/plot-constants.ts';

export const GENERIC_ENGINE = 'Generic';
const SEP = '‖'; // ‖ - won't collide with ship ids / DG labels / fuel names

/** Preferred fuel column order; unknown fuels sort last, alphabetically. */
const FUEL_ORDER = ['LNG', 'MGO', 'HFO', 'VLSFO', 'IGNITION', 'B100'];
export function fuelRank(fuel: string): number {
  const i = FUEL_ORDER.indexOf(fuel);
  return i === -1 ? FUEL_ORDER.length : i;
}

/**
 * Fuel colours are the maritime domain palette (visual-identity.md §8.4),
 * defined once as `--fuel-*` tokens in tokens.css with a variant per theme. The
 * sidebar cells use the variable directly; the chart reads its value.
 */
const FUEL_TOKENS: Record<string, string> = {
  LNG: '--fuel-lng',
  MGO: '--fuel-mgo',
  HFO: '--fuel-hfo',
  VLSFO: '--fuel-vlsfo',
  B100: '--fuel-b100',
  IGNITION: '--fuel-ignition',
};

/** The CSS variable holding a fuel's colour, or null for a fuel not in the palette. */
export function fuelVar(fuel: string): string | null {
  return FUEL_TOKENS[fuel.toUpperCase()] ?? null;
}

/**
 * A shade of a fuel's colour for the `idx`-th of `count` curves on that fuel,
 * so DG1, DG2 and DG3 on LNG stay one family and still tell apart. Spread
 * around the base's own lightness rather than across a fixed band: the palette
 * runs from pale LNG to dark B100, and a fixed band would wash one out and sink
 * the other. The first curve gets the base itself.
 */
export function fuelShade(base: string, idx: number, count: number): string {
  if (count <= 1 || idx === 0) return base;
  const [h, sat, l] = hexToHsl(base);
  const spread = Math.min(0.12, 0.3 / Math.max(1, count - 1));
  // A pale base (dark-theme LNG) only has room to go darker, a deep one only
  // lighter; a mid one alternates, lighter then darker.
  let step: number;
  if (l > 0.6) step = -idx;
  else if (l < 0.4) step = idx;
  else step = Math.ceil(idx / 2) * (idx % 2 ? 1 : -1);
  const shaded = Math.max(0.28, Math.min(0.86, l + step * spread));
  return hslToHex(h, sat, shaded);
}

export function sortFuels(fuels: Iterable<string>): string[] {
  return [...new Set(fuels)].sort((a, b) => fuelRank(a) - fuelRank(b) || a.localeCompare(b));
}

export function curveKey(ship: string, engine: string, fuel: string): string {
  return `${ship}${SEP}${engine}${SEP}${fuel}`;
}
export interface CurveRef {
  ship: string;
  engine: string;
  fuel: string;
}
export function parseKey(key: string): CurveRef {
  const [ship, engine, fuel] = key.split(SEP);
  return { ship: ship ?? '', engine: engine ?? '', fuel: fuel ?? '' };
}

export interface MatrixRow {
  engine: string;
  /** Fuels this engine actually has a curve for. */
  fuels: Set<string>;
}
export interface ShipMatrix {
  rows: MatrixRow[];
  /** Union of fuels across rows, in display order (the column set). */
  fuels: string[];
  /** True when this ship only has generic curves (single synthetic row). */
  generic: boolean;
}

/** Build the engine×fuel matrix for one ship's fetched curves. */
export function shipMatrix(sd: SfocCurves): ShipMatrix {
  if (sd.engines.length) {
    const rows = sd.engines.map((e) => ({
      engine: e.label,
      fuels: new Set(e.curves.map((c) => c.fuel_type)),
    }));
    const fuels = sortFuels(rows.flatMap((r) => [...r.fuels]));
    return { rows, fuels, generic: false };
  }
  const fuels = sortFuels(sd.generic.map((c) => c.fuel_type));
  return { rows: [{ engine: GENERIC_ENGINE, fuels: new Set(fuels) }], fuels, generic: true };
}

/** Look up the curve for an (engine, fuel) cell, or undefined if absent. */
export function getCurve(sd: SfocCurves, engine: string, fuel: string): SfocCurve | undefined {
  if (engine === GENERIC_ENGINE) return sd.generic.find((c) => c.fuel_type === fuel);
  return sd.engines.find((e) => e.label === engine)?.curves.find((c) => c.fuel_type === fuel);
}

/** Every curve key for a ship (all engine×fuel cells that exist). */
export function keysForShip(sd: SfocCurves, ship: string): string[] {
  const m = shipMatrix(sd);
  const out: string[] = [];
  for (const row of m.rows) {
    for (const fuel of row.fuels) out.push(curveKey(ship, row.engine, fuel));
  }
  return out;
}
export function keysForEngine(sd: SfocCurves, ship: string, engine: string): string[] {
  const row = shipMatrix(sd).rows.find((r) => r.engine === engine);
  return row ? [...row.fuels].map((f) => curveKey(ship, engine, f)) : [];
}
export function keysForFuel(sd: SfocCurves, ship: string, fuel: string): string[] {
  return shipMatrix(sd)
    .rows.filter((r) => r.fuels.has(fuel))
    .map((r) => curveKey(ship, r.engine, fuel));
}

export type TriState = 'none' | 'partial' | 'all';
export function triState(keys: string[], selected: Set<string>): TriState {
  if (keys.length === 0) return 'none';
  let on = 0;
  for (const k of keys) if (selected.has(k)) on++;
  if (on === 0) return 'none';
  return on === keys.length ? 'all' : 'partial';
}

/**
 * The version of a curve in force on `date` (YYYY-MM-DD): the latest whose
 * `valid_from` is on or before it. Null when every stored version started
 * later - the curve did not exist yet, and nothing is drawn rather than a model
 * from the future. A curve fetched without history is its own only version.
 */
export function versionAt(curve: SfocCurve, date: string): SfocCurveVersion | null {
  const versions = curve.versions?.length
    ? curve.versions
    : [{ valid_from: curve.valid_from, terms: curve.terms, r2: curve.r2 }];
  let found: SfocCurveVersion | null = null;
  for (const v of versions) {
    if (!v.valid_from || v.valid_from <= date) found = v;
  }
  return found;
}
